import React from "react";
import Text from "@src/lib/ui/Text";
import { Card } from "@src/lib/ui/Card";
import { cn } from "@src/lib/utils/cn";
import Link from "next/link";
import { Route } from "nextjs-routes";
import { ActionPermissionString } from "@src/features/app/Page";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";

type Props = {
  title: string;
  Icon?: React.ElementType;
  className?: string;
  children: React.ReactNode;
  href?: Route;
  /** Drop the card link unless the user has this permission; the destination page 404s without it. */
  hrefPermission?: ActionPermissionString;
};

const Widget: React.FC<Props> = ({
  title,
  Icon,
  children,
  className,
  href: hrefProp,
  hrefPermission,
}) => {
  const { userActionPermissions } = useActionPermissions();
  const href =
    hrefPermission && !userActionPermissions?.[hrefPermission]
      ? undefined
      : hrefProp;
  // The card-level link is a "stretched link" (an overlay rendered from the
  // header anchor) rather than an <a> wrapping the card, so widgets can
  // render their own links in children without nesting anchors.
  return (
    <Card
      variant="default"
      className={cn(
        "relative flex h-full flex-col gap-6 rounded-lg border-gray-2 bg-transparent p-4 shadow-none",
        href && "transition-colors hover:bg-gray-1",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon className="text-muted-foreground" />}
        {href ? (
          <Link
            href={href}
            className="no-underline before:absolute before:inset-0 before:content-['']"
          >
            <Text variant="compact-title">{title}</Text>
          </Link>
        ) : (
          <Text variant="compact-title">{title}</Text>
        )}
      </div>
      {href ? (
        <div className="relative flex min-h-0 flex-1 flex-col gap-6">
          {children}
        </div>
      ) : (
        children
      )}
    </Card>
  );
};

export default Widget;
